import { useEffect, useRef, useState } from 'react';

import { Box, Collapse, Divider, Group, ScrollArea, Table, Tabs, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';

import {
  useArchiveNoteMutation,
  useGetNotesByPatientIdQuery,
  useGetPinnedNotesQuery,
  useUnArchiveNoteMutation,
  useUpdateNoteMutation
} from '@/store/slices/api/clinicalNotesApiSlice';
import { NotesTabType } from '@/utils/constants';
import formatDate from '@/utils/formatDate';
import ArchiveIcon from '@/assets/icons/ArchiveIcon';
import KaretDownIcon from '@/assets/icons/KaretDownIcon';
import KaretUpIcon from '@/assets/icons/KaretUpIcon';

import { ClinicalNote } from '@/types/clinicalNote';
import ConfirmationModal from '@/ui/ConfirmationModal';
import IconButton from '@/ui/IconButton';
import SkeletonGrid from '@/ui/Skeleton';
import TextWrapper from '@/ui/TextWrapper';

import AddNotesModal from './AddNotesModal';
import { DateCell, NoteCell, OwnerCell } from './Cells';
import classes from './ClinicalNotes.module.scss';

interface ClinicalNotesProps {
  patientId: string;
  onlyPinnedNotes?: boolean;
}

const ClinicalNotes = ({ patientId, onlyPinnedNotes = false }: ClinicalNotesProps) => {
  const [selectedTab, setSelectedTab] = useState<NotesTabType>(NotesTabType.All);
  const [localNotes, setLocalNotes] = useState<ClinicalNote[]>([]);
  const [localPinnedNotes, setLocalPinnedNotes] = useState<ClinicalNote[]>([]);
  const [localArchivedNotes, setLocalArchivedNotes] = useState<ClinicalNote[]>([]);
  const [selectedNoteToArchive, setSelectedNoteToArchive] = useState<ClinicalNote | null>(null);
  const [selectedNoteToUnarchive, setSelectedNoteToUnarchive] = useState<ClinicalNote | null>(null);
  const [selectedNoteToEdit, setSelectedNoteToEdit] = useState<ClinicalNote | null>(null);
  const [pinLoadingId, setPinLoadingId] = useState<string>('');

  // Add refs to track if we've already processed this data
  const processedNotesRef = useRef<string>('');
  const processedPinnedNotesRef = useRef<string>('');

  const [addNoteModalOpened, { open: openAddNoteModal, close: closeAddNoteModal }] =
    useDisclosure(false);
  const [editNoteModalOpened, { open: openEditNoteModal, close: closeEditNoteModal }] =
    useDisclosure(false);
  const [archiveModalOpened, { open: openArchiveModal, close: closeArchiveModal }] =
    useDisclosure(false);
  const [unarchiveModalOpened, { open: openUnarchiveModal, close: closeUnarchiveModal }] =
    useDisclosure(false);
  const [archivedSectionOpened, { toggle: toggleArchivedSection }] = useDisclosure(false);

  const {
    data: notesData = { notes: [] },
    isLoading: isNotesLoading,
    refetch: refetchNotes
  } = useGetNotesByPatientIdQuery(
    { patientId, limit: 50, page: 1 },
    { skip: !patientId, refetchOnMountOrArgChange: true }
  );

  const { data: pinnedNotesData, refetch: refetchPinnedNotes } = useGetPinnedNotesQuery(
    { patientId },
    { skip: !patientId, refetchOnMountOrArgChange: true }
  );

  const [archiveNote, { isLoading: isArchieving }] = useArchiveNoteMutation();
  const [unArchiveNote, { isLoading: isUnarchiving }] = useUnArchiveNoteMutation();
  const [updateNote, { isLoading: isUpdating }] = useUpdateNoteMutation();

  useEffect(() => {
    if (notesData?.notes) {
      // Create a hash to detect actual changes
      const notesHash = JSON.stringify(
        notesData.notes.map((n) => ({ id: n.id, isArchived: n.isArchived, isPinned: n.isPinned }))
      );

      // Only update if the data actually changed
      if (processedNotesRef.current !== notesHash) {
        processedNotesRef.current = notesHash;

        // Filter archived and non-archived notes
        const activeNotes = notesData.notes.filter((note) => !note.isArchived);
        const archivedNotes = notesData.notes.filter((note) => note.isArchived);

        setLocalNotes(activeNotes);
        setLocalArchivedNotes(archivedNotes);
      }
    }
  }, [notesData]);

  useEffect(() => {
    if (pinnedNotesData?.pinnedNotes) {
      // Create a hash to detect actual changes
      const pinnedHash = JSON.stringify(
        pinnedNotesData.pinnedNotes.map((n) => ({
          id: n.id,
          isArchived: n.isArchived,
          isPinned: n.isPinned
        }))
      );

      // Only update if the data actually changed
      if (processedPinnedNotesRef.current !== pinnedHash) {
        processedPinnedNotesRef.current = pinnedHash;

        // Filter out archived notes from pinned notes
        const activePinnedNotes = pinnedNotesData.pinnedNotes.filter((note) => !note.isArchived);
        setLocalPinnedNotes(activePinnedNotes);
      }
    }
  }, [pinnedNotesData]);

  const showNotification = (title: string, message: string, color: string) => {
    notifications.show({
      title,
      message,
      color
    });
  };

  const refreshNotesData = async () => {
    try {
      await Promise.all([refetchNotes(), refetchPinnedNotes()]);
    } catch (error) {
      console.error('Failed to refresh notes data:', error);
    }
  };

  const getFilteredNotes = () => {
    switch (selectedTab) {
      case NotesTabType.Standard:
        return localNotes.filter((note) => !note.isPinned);
      case NotesTabType.Pinned:
        // Only show pinned notes that are not archived
        return localPinnedNotes.filter((note) => !note.isArchived);
      default:
        return localNotes;
    }
  };

  const filteredNotes = getFilteredNotes();

  const handleEditClick = (note: ClinicalNote) => {
    setSelectedNoteToEdit(note);
    openEditNoteModal();
  };

  const handleCloseEditModal = () => {
    closeEditNoteModal();
    setSelectedNoteToEdit(null);
  };

  const handleArchiveClick = (note: ClinicalNote) => {
    setSelectedNoteToArchive(note);
    openArchiveModal();
  };

  const handleUnarchiveClick = (note: ClinicalNote) => {
    setSelectedNoteToUnarchive(note);
    openUnarchiveModal();
  };

  const handleConfirmArchive = async () => {
    if (!selectedNoteToArchive) return;

    try {
      await archiveNote({
        noteId: selectedNoteToArchive.id,
        patientId
      }).unwrap();

      // Move note from active to archived
      if (selectedNoteToArchive.isPinned) {
        setLocalPinnedNotes((prev) => prev.filter((note) => note.id !== selectedNoteToArchive.id));
      }
      setLocalNotes((prev) => prev.filter((note) => note.id !== selectedNoteToArchive.id));
      setLocalArchivedNotes((prev) => [...prev, { ...selectedNoteToArchive, isArchived: true }]);

      await refreshNotesData();

      showNotification('Success', 'Note archived successfully', 'green');
      closeArchiveModal();
      setSelectedNoteToArchive(null);
    } catch (error) {
      console.error('Failed to archive note:', error);
      showNotification('Error', 'Failed to archive note. Please try again.', 'red');
    }
  };

  const handleConfirmUnarchive = async () => {
    if (!selectedNoteToUnarchive) return;

    try {
      await unArchiveNote({
        noteId: selectedNoteToUnarchive.id,
        patientId
      }).unwrap();

      // Move note from archived to active
      const unarchivedNote = { ...selectedNoteToUnarchive, isArchived: false };
      setLocalArchivedNotes((prev) =>
        prev.filter((note) => note.id !== selectedNoteToUnarchive.id)
      );
      setLocalNotes((prev) => [...prev, unarchivedNote]);

      // If the note was pinned, add it back to pinned notes
      if (selectedNoteToUnarchive.isPinned) {
        setLocalPinnedNotes((prev) => [...prev, unarchivedNote]);
      }

      await refreshNotesData();

      showNotification('Success', 'Note unarchived successfully', 'green');
      closeUnarchiveModal();
      setSelectedNoteToUnarchive(null);
    } catch (error) {
      console.error('Failed to unarchive note:', error);
      showNotification('Error', 'Failed to unarchive note. Please try again.', 'red');
    }
  };

  const handleTogglePin = async (clinicalNote: ClinicalNote) => {
    if (pinLoadingId === clinicalNote.id) return; // Prevent double clicks

    setPinLoadingId(clinicalNote.id);

    try {
      await updateNote({
        noteId: clinicalNote.id,
        isPinned: !clinicalNote.isPinned,
        patientId
      }).unwrap();

      // Update the note in all relevant local states
      setLocalNotes((prev) =>
        prev.map((n) => (n.id === clinicalNote.id ? { ...n, isPinned: !clinicalNote.isPinned } : n))
      );

      setLocalArchivedNotes((prev) =>
        prev.map((n) => (n.id === clinicalNote.id ? { ...n, isPinned: !clinicalNote.isPinned } : n))
      );

      if (clinicalNote.isPinned) {
        setLocalPinnedNotes((prev) => prev.filter((n) => n.id !== clinicalNote.id));
      } else {
        setLocalPinnedNotes((prev) => [...prev, { ...clinicalNote, isPinned: true }]);
      }

      await refreshNotesData();

      showNotification(
        'Success',
        `Note ${clinicalNote.isPinned ? 'unpinned' : 'pinned'} successfully`,
        'green'
      );
    } catch (error) {
      console.error('Failed to update note pin status:', error);
      showNotification(
        'Error',
        `Failed to ${clinicalNote.isPinned ? 'unpin' : 'pin'} note. Please try again.`,
        'red'
      );
    } finally {
      setPinLoadingId('');
    }
  };

  // Check if patientId is provided
  if (!patientId) return <div>No Patient ID provided.</div>;

  const renderLoadingState = () => (
    <Table.Tr>
      <Table.Td colSpan={4}>
        <SkeletonGrid columns={1} rows={3} height={30} />
      </Table.Td>
    </Table.Tr>
  );

  const renderEmptyState = () => (
    <Table.Tr>
      <Table.Td colSpan={4} align="center" py="xl">
        <Text className={classes.emptyState}>No clinical notes found</Text>
      </Table.Td>
    </Table.Tr>
  );

  const renderNoteRows = (notes: ClinicalNote[], isArchived = false) =>
    notes.flatMap((note: ClinicalNote) => [
      <Table.Tr key={note.id}>
        <Table.Td w="15%">
          <DateCell value={note.createdAt} />
        </Table.Td>
        <Table.Td w="20%">
          <OwnerCell
            value={{
              given: note.createdBy.given,
              family: note.createdBy.family
            }}
          />
        </Table.Td>
        <Table.Td w="65%">
          <NoteCell value={note.content} />

          <div className={classes.iconsContainer}>
            {!onlyPinnedNotes && (
              <IconButton icon="edit" onClick={() => handleEditClick(note)} label="Edit" />
            )}
            {!onlyPinnedNotes &&
              (isArchived ? (
                <IconButton
                  icon="unArchive"
                  onClick={() => handleUnarchiveClick(note)}
                  label="Unarchive"
                />
              ) : (
                <IconButton
                  icon="archive"
                  onClick={() => handleArchiveClick(note)}
                  label="Archive"
                />
              ))}
            <IconButton
              icon={note.isPinned ? 'pinned' : 'pin'}
              onClick={() => handleTogglePin(note)}
              label={note.isPinned ? 'Unpin' : 'Pin'}
              className={classes.iconBtn}
              disabled={isUpdating || pinLoadingId === note.id}
            />
          </div>
        </Table.Td>
      </Table.Tr>,
      ...(note.updatedBy && note.updatedAt
        ? [
            <Table.Tr key={`${note.id}-updated`}>
              <Table.Td colSpan={3} style={{ paddingTop: 0 }}>
                <Text size="xs" c="dimmed">
                  Last Updated by {note.updatedBy.given} {note.updatedBy.family} •{' '}
                  {formatDate(note.updatedAt, 'MM/DD/YYYY, hh:mm A')}
                </Text>
                <Divider mt="xs" />
              </Table.Td>
            </Table.Tr>
          ]
        : [])
    ]);

  const tableBody = () => {
    if (isNotesLoading) {
      return renderLoadingState();
    }

    if (filteredNotes.length === 0) {
      return renderEmptyState();
    }

    return renderNoteRows(filteredNotes);
  };

  const tableHeader = () => (
    <Table.Thead className={classes.heading}>
      <Table.Tr>
        <Table.Th className={classes.tableHeader} w="15%">
          DATE
        </Table.Th>
        <Table.Th className={classes.tableHeader} w="20%">
          USER
        </Table.Th>
        <Table.Th className={classes.tableHeader} w="65%">
          NOTE
        </Table.Th>
        <Table.Th></Table.Th>
      </Table.Tr>
    </Table.Thead>
  );

  const tabs = () => (
    <Tabs
      variant="none"
      value={selectedTab}
      onChange={(value) => setSelectedTab(value as NotesTabType)}>
      <Tabs.List>
        <Tabs.Tab value={NotesTabType.All} className={classes.tab}>
          All
        </Tabs.Tab>
        <Tabs.Tab value={NotesTabType.Pinned} className={classes.tab}>
          <Group gap={4}>Pinned</Group>
        </Tabs.Tab>
      </Tabs.List>
    </Tabs>
  );

  const header = () => (
    <div className={classes.header}>
      {tabs()}
      <IconButton icon="plus" label="Add Note" onClick={openAddNoteModal} />
    </div>
  );

  const getFilteredArchivedNotes = () => {
    switch (selectedTab) {
      case NotesTabType.Pinned:
        return localArchivedNotes.filter((note) => note.isPinned);
      default:
        return localArchivedNotes;
    }
  };

  const filteredArchivedNotes = getFilteredArchivedNotes();

  const archivedSection = () => (
    <Box mt="md">
      <Divider />
      <Group onClick={toggleArchivedSection} style={{ cursor: 'pointer' }} mb="4" mt="4">
        <ArchiveIcon />
        <Text className={classes.collapseHeaderTitle}>Archived</Text>
        {archivedSectionOpened ? <KaretUpIcon /> : <KaretDownIcon />}
      </Group>
      <Divider />

      <Collapse in={archivedSectionOpened}>
        <Table withColumnBorders={false} withRowBorders={false}>
          <Table.Tbody className={classes.tbody}>
            {filteredArchivedNotes.length === 0 ? (
              <Table.Tr>
                <Table.Td colSpan={4} align="center" py="xl">
                  <Text className={classes.emptyState}>No archived notes found</Text>
                </Table.Td>
              </Table.Tr>
            ) : (
              renderNoteRows(filteredArchivedNotes, true)
            )}
          </Table.Tbody>
        </Table>
      </Collapse>
    </Box>
  );

  if (onlyPinnedNotes) {
    return (
      <div className={classes.reportSection}>
        <div className={classes.pinnedNotesHeader}>
          <TextWrapper text="Pinned Notes" variant="heading" />
        </div>
        <ScrollArea h={localPinnedNotes.length === 0 ? 150 : 280} scrollbarSize={5}>
          <Table withColumnBorders={false} withRowBorders={false}>
            {tableHeader()}
            <Table.Tbody className={classes.tbody}>
              {localPinnedNotes.length === 0 ? (
                <Table.Tr>
                  <Table.Td colSpan={4} align="center" py="xl">
                    <Text className={classes.emptyState}>No pinned notes found</Text>
                  </Table.Td>
                </Table.Tr>
              ) : (
                renderNoteRows(localPinnedNotes)
              )}
            </Table.Tbody>
          </Table>
        </ScrollArea>
      </div>
    );
  }

  return (
    <Box>
      {header()}

      <Box>
        <Table withColumnBorders={false} withRowBorders={false}>
          {tableHeader()}
          <Table.Tbody className={classes.tbody}>{tableBody()}</Table.Tbody>
        </Table>
      </Box>

      {archivedSection()}

      <AddNotesModal
        isOpen={addNoteModalOpened || editNoteModalOpened}
        onClose={editNoteModalOpened ? handleCloseEditModal : closeAddNoteModal}
        patientId={patientId}
        noteToEdit={selectedNoteToEdit}
      />

      <ConfirmationModal
        isOpen={archiveModalOpened}
        isLoading={isArchieving}
        onClose={closeArchiveModal}
        onConfirm={handleConfirmArchive}
        title="Archive this note?"
        primaryButtonText="Yes, Archive"
        description="This note will be moved to the Archived, You can restore it later from the Archived notes section."
      />

      <ConfirmationModal
        isOpen={unarchiveModalOpened}
        isLoading={isUnarchiving}
        onClose={closeUnarchiveModal}
        onConfirm={handleConfirmUnarchive}
        title="Unarchive this note?"
        primaryButtonText="Yes, Unarchive"
        description="This note will be restored to your active notes."
      />
    </Box>
  );
};

export default ClinicalNotes;
